import React, { useEffect, useState } from 'react';
import './singleExamReports.css';
import './reports-beautiful.css';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { useGetAllCommonStudentInitialDataMutation, usePostCommonMutation } from 'slices/get/common';
import useService from 'views/default/useService';
import { Card, Button, ProgressBar } from 'react-bootstrap';
import { PageHeader, LoadingSpinner, NoDataFound } from './CommonComponents';
import './common-components.css';

const SingleExamReport = () => {
  const { checkAccessTime, fetchAccessToken } = useService();
  const [getAllCommonStudentInitialData] = useGetAllCommonStudentInitialDataMutation();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const assessmentType = query.get('id');
  const currentPath = location.pathname;
  const history = useHistory();
  const [postCommon] = usePostCommonMutation();

  useEffect(() => {
    setLoading(true);
    const access_token = fetchAccessToken();
    let values = {
      assessment_type: assessmentType
    };

    postCommon({ ep: `assessment/reports_single`, values, token: access_token }).then(
      (response) => {
        if (response && response?.data && response.data.status) {
          setReportData(response.data.data);
        }
        setLoading(false);
        checkAccessTime(response, currentPath);
      }
    ).catch(() => {
      setLoading(false);
    });
  }, []);

  // Get grade color
  const getGradeColor = (grade) => {
    if (!grade) return '#888';
    if (grade.startsWith('A')) return '#11998e';
    if (grade.startsWith('B')) return '#667eea';
    if (grade.startsWith('C')) return '#f7971e';
    return '#ff416c';
  };

  // Get percentage color
  const getPercentageColor = (obtained, total) => {
    const percentage = (obtained / total) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'info';
    if (percentage >= 40) return 'warning';
    return 'danger';
  };

  // Show loading state
  if (loading) {
    return (
      <div className="report-page-beautiful">
        <LoadingSpinner message="Loading report..." fullPage={true} />
      </div>
    );
  }

  // Show no data state
  if (!reportData || !reportData.subjects || reportData.subjects.length === 0) {
    return (
      <div className="report-page-beautiful">
        <NoDataFound
          title="Report Not Available"
          message="This progress report is not available yet. Please check back later."
          icon="file-earmark-x"
          showBackButton={true}
        />
      </div>
    );
  }

  // Calculate totals
  const totalObtained = reportData.subjects.reduce((sum, s) => sum + (s.obtainedMarks || 0), 0);
  const totalMarks = reportData.subjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0);
  const percentage = totalMarks > 0 ? Math.round((totalObtained / totalMarks) * 100) : 0;
  const passed = reportData.subjects.filter(s => s.pass).length;
  const failed = reportData.subjects.filter(s => !s.pass).length;

  return (
    <div className="report-page-beautiful">
      {/* Beautiful Header with Back Button */}
      <PageHeader
        title="Exam Report"
        subtitle={`Progress Report for ${reportData.name || 'Student'}`}
        icon="clipboard-data"
        showBackButton={true}
        gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
      />

      {/* Report Card */}
      <Card className="report-card-container">
        {/* School Header */}
        <div className="school-header-beautiful single-exam">
          <div className="school-logo-placeholder">
            <i className="bi bi-mortarboard-fill"></i>
          </div>
          <h1 className="school-name-beautiful">{reportData.school}</h1>
          <p className="school-address-beautiful">{reportData.address}</p>
          <div className="report-title-badge exam-badge">
            <i className="bi bi-clipboard-check me-2"></i>
            Formative Assessment Report
          </div>
        </div>

        {/* Student Info with Stats */}
        <div className="student-info-beautiful with-stats">
          <div className="student-details-beautiful">
            <div className="detail-item">
              <span className="detail-label">Student Name</span>
              <span className="detail-value">{reportData.name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Standard</span>
              <span className="detail-value">{reportData.standard}</span>
            </div>
            <div className="detail-item">
              <span className="detail-label">Section</span>
              <span className="detail-value">{reportData.section}</span>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="report-stats-grid">
            <div className="report-stat-card percentage">
              <div className="stat-value">{percentage}%</div>
              <div className="stat-label">Overall</div>
            </div>
            <div className="report-stat-card marks">
              <div className="stat-value">{totalObtained}/{totalMarks}</div>
              <div className="stat-label">Total Marks</div>
            </div>
            <div className="report-stat-card passed">
              <div className="stat-value">{passed}</div>
              <div className="stat-label">Passed</div>
            </div>
            <div className="report-stat-card failed">
              <div className="stat-value">{failed}</div>
              <div className="stat-label">Failed</div>
            </div>
          </div>
        </div>

        {/* Marks Table */}
        <div className="table-wrapper-beautiful">
          <table className="report-table-beautiful single-exam-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Total Marks</th>
                <th>Obtained</th>
                <th>Progress</th>
                <th>Grade</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {reportData.subjects.map((subject, index) => {
                const subjectPercentage = subject.totalMarks > 0 
                  ? Math.round((subject.obtainedMarks / subject.totalMarks) * 100) 
                  : 0;
                return (
                  <tr key={index} style={{ animationDelay: `${index * 0.05}s` }}>
                    <td className="subject-name-cell">{subject.name}</td>
                    <td>{subject.totalMarks}</td>
                    <td className="obtained-marks">{subject.obtainedMarks}</td>
                    <td className="progress-cell">
                      <div className="progress-wrapper">
                        <ProgressBar 
                          now={subjectPercentage} 
                          variant={getPercentageColor(subject.obtainedMarks, subject.totalMarks)}
                          className="marks-progress"
                        />
                        <span className="progress-text">{subjectPercentage}%</span>
                      </div>
                    </td>
                    <td>
                      <span className="grade-badge" style={{ background: getGradeColor(subject.grade) }}>
                        {subject.grade}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge-report ${subject.pass ? 'pass' : 'fail'}`}>
                        {subject.pass ? (
                          <><i className="bi bi-check-circle-fill me-1"></i>Pass</>
                        ) : (
                          <><i className="bi bi-x-circle-fill me-1"></i>Fail</>
                        )}
                      </span>
                    </td>
                    <td className="remarks-cell">{subject.remarks}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Signatures */}
        <div className="signatures-beautiful">
          <div className="signature-item">
            <div className="signature-line"></div>
            <p>Teacher's Signature</p>
          </div>
          <div className="signature-item">
            <div className="signature-line"></div>
            <p>Parent's Signature</p>
          </div>
        </div>

        {/* Print Button */}
        <div className="report-actions">
          <Button className="print-button-beautiful" onClick={() => window.print()}>
            <i className="bi bi-printer me-2"></i>
            Print Report
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default SingleExamReport;
